// Relative path: src/components/Recommendations/ClinicInfoText.js
import React, { useState } from 'react';
import DetailsPopup from '../Popup/DetailsPopup';
import ReactDOM from 'react-dom';
import ClinicHighlights from './ClinicHighlights';

// Import SVG icons for the table
import estimatedCostIcon from '../../images/estimated_cost.svg';
import estimatedAvailabilityIcon from '../../images/estimated_availability.svg';

const ClinicInfoText = ({ 
  optionTitle = 'Option Title', 
  optionDescription = 'Option description goes here',
  optionDescriptionLong = '', 
  estimatedCost = '3000-6000', 
  estimatedAvailability = 'Within 2 Months',
  clinicHighlights = { 
    loading: true, 
    reviewCount: 0, 
    rating: 4.5,
    websiteReviewCounts: {},
    totalReviewCount: 0
  }
}) => {
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  
  // Create a portal for the description popup
  const renderDescriptionPopup = () => {
    if (!isDescriptionOpen) return null;
    
    // Prepare paragraphs array, filtering out empty strings
    const paragraphs = [
      optionDescription, // Short description first
      optionDescriptionLong // Long description second
    ].filter(Boolean);
    
    if (typeof document !== 'undefined') {
      return ReactDOM.createPortal(
        <DetailsPopup 
          isOpen={isDescriptionOpen}
          onClose={() => setIsDescriptionOpen(false)}
          title={`${optionTitle} Description`}
          paragraphs={paragraphs}
        />,
        document.body
      );
    }
    
    return null;
  };
  
  return (
    <div className="w-full mb-6">
      {/* Title */}
      <h3 className="font-bold text-gray-800 text-3xl mb-2" title={optionTitle}>
        {optionTitle}
      </h3>
      
      {/* Description - Made clickable */}
      <p 
        className="text-gray-600 text-md leading-[22px] mb-4 cursor-pointer hover:text-gray-800"
        style={{ 
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxHeight: '4.5em', // Approximately 3 lines of text
        }}
        onClick={() => setIsDescriptionOpen(true)}
      >
        {optionDescription}... Show more. 
      </p>
      
      {/* Cost and Availability Info - Mobile Layout (unchanged) */}
      <div className="flex flex-wrap md:hidden">
        {/* Estimated Cost */}
        <div className="flex items-center mr-8 mb-2">
          <img 
            src={estimatedCostIcon}
            alt="Estimated Cost Icon" 
            className="w-8 h-8 mr-2"
          />
          <div>
            <div className="text-md font-bold text-gray-800">Estimated Cost:</div>
            <div className="text-md text-green-600 font-medium">${estimatedCost}</div>
          </div>
        </div>
        
        {/* Estimated Availability */}
        <div className="flex items-center mb-2">
          <img 
            src={estimatedAvailabilityIcon}
            alt="Estimated Availability Icon" 
            className="w-8 h-8 mr-2"
          />
          <div>
            <div className="text-md font-bold text-gray-800">Availability:</div>
            <div className="text-md text-green-600 font-medium">{estimatedAvailability}</div>
          </div>
        </div>
      </div>

      {/* Cost and Availability Info - Desktop Layout (new design) */}
      <div className="hidden md:flex bg-transparent rounded-2xl border-2 border-gray-200 overflow-hidden">
        {/* Estimated Cost Section */}
        <div className="flex-1 px-6 py-4 flex items-center">
          <div className="w-12 h-12 flex items-center justify-center mr-4 flex-shrink-0">
            <img 
              src={estimatedCostIcon}
              alt="Estimated Cost Icon" 
              className="w-10 h-10"
            />
          </div>
          <div className="flex-1">
            <div className="text-base font-bold text-gray-600 mb-1">Price Range</div>
            <div className="text-sm font-base text-gray-700">${estimatedCost}</div>
          </div>
        </div>
        
        {/* Vertical Divider */}
        <div className="w-px my-2 bg-gray-200"></div>
        
        {/* Estimated Availability Section */}
        <div className="flex-1 px-6 py-4 flex items-center">
          <div className="w-12 h-12 flex items-center justify-center mr-4 flex-shrink-0">
            <img 
              src={estimatedAvailabilityIcon}
              alt="Estimated Availability Icon" 
              className="w-10 h-10"
            />
          </div>
          <div className="flex-1">
            <div className="text-base font-bold text-gray-600 mb-1">Soonest Availability</div>
            <div className="text-sm font-base text-gray-700">{estimatedAvailability}</div>
          </div>
        </div>
      </div>

      {/* Clinic Highlights Section - Desktop Only */}
      <div className="hidden md:block">
        {clinicHighlights.loading ? (
          <div className="flex bg-white rounded-2xl border border-gray-200 overflow-hidden mt-4">
            <div className="animate-pulse flex items-center justify-between w-full p-6">
              <div className="flex-1 flex flex-col items-center">
                <div className="bg-gray-200 rounded h-20 w-32 mb-2" />
              </div>
              <div className="w-px my-2 bg-gray-200" />
              <div className="flex-1 flex flex-col items-center">
                <div className="bg-gray-200 rounded h-8 w-16 mb-2" />
                <div className="bg-gray-200 rounded h-6 w-24" />
              </div>
              <div className="w-px my-2 bg-gray-200" />
              <div className="flex-1 flex flex-col items-center">
                <div className="bg-gray-200 rounded h-8 w-12 mb-2" />
                <div className="bg-gray-200 rounded h-6 w-20" />
              </div>
            </div>
          </div>
        ) : (
          <ClinicHighlights
            rating={clinicHighlights.rating}
            reviewCount={clinicHighlights.reviewCount}
            websiteReviewCounts={clinicHighlights.websiteReviewCounts || {}}
            totalReviewCount={clinicHighlights.totalReviewCount || clinicHighlights.reviewCount || 0}
            isMobile={false}
          />
        )}
      </div>
      
      {/* Description Popup */}
      {renderDescriptionPopup()}
    </div>
  );
};

export default ClinicInfoText;